import type { CaseAttributeDto, CaseAttributeKind } from '@tms/shared';
import { Layers, Pencil, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  ConfirmDialog,
  Menu,
  MenuItem,
  MenuLabel,
  SkeletonRows,
} from '../../components/ui';
import { useErrorText, useT } from '../../i18n';
import { usePermissions } from '../auth/permissions';
import { CaseAttributeFormDialog } from './CaseAttributeFormDialog';
import { useCaseAttributes, useDeleteCaseAttribute } from './api';
import { CASE_ATTRIBUTE_MESSAGES } from './messages';

/** Neutral chips, like the environments above: these have no colour either. */
const chipClassName =
  'inline-flex max-w-48 items-center truncate rounded-md border border-border bg-sunken px-1.5 py-0.5 ' +
  'text-[12px] font-medium text-ink outline-none ring-offset-1 ring-offset-surface ' +
  'transition-colors hover:border-border-strong focus-visible:ring-2 focus-visible:ring-accent';

/**
 * Administration of one of the two case catalogues: add, rename, remove. The
 * layers and the modules are the same card twice — only the words differ.
 */
export const CaseAttributesCard = ({
  projectCode,
  kind,
}: {
  projectCode: string;
  kind: CaseAttributeKind;
}) => {
  const { t } = useT();
  const { can } = usePermissions();
  const { errorText } = useErrorText();
  const text = CASE_ATTRIBUTE_MESSAGES[kind];
  const { data: attributes, isPending } = useCaseAttributes(projectCode, kind);
  const remove = useDeleteCaseAttribute(projectCode, kind);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<CaseAttributeDto | undefined>(undefined);
  const [deleting, setDeleting] = useState<CaseAttributeDto | undefined>(undefined);

  const canManage = can('attribute:manage');

  const openCreate = (): void => {
    setEditing(undefined);
    setFormOpen(true);
  };

  const openEdit = (attribute: CaseAttributeDto): void => {
    setEditing(attribute);
    setFormOpen(true);
  };

  const confirmDelete = async (): Promise<void> => {
    if (!deleting) return;
    try {
      await remove.mutateAsync(deleting.id);
      toast.success(t(text.deleted, { name: deleting.name }));
      setDeleting(undefined);
    } catch (error) {
      toast.error(errorText(error, text.deleteFailed));
    }
  };

  return (
    <>
      <Card>
        <CardHeader
          title={t(text.title)}
          description={t(text.hint)}
          action={
            canManage ? (
              <Button size="sm" onClick={openCreate}>
                <Plus className="size-3.5" />
                {t(text.new)}
              </Button>
            ) : null
          }
        />
        <CardBody>
          {isPending ? (
            <SkeletonRows rows={2} className="p-0" />
          ) : (attributes ?? []).length === 0 ? (
            <p className="flex items-center gap-2 text-[13px] text-ink-subtle">
              <Layers className="size-3.5" />
              {t(text.empty)}
            </p>
          ) : (
            <ul className="flex flex-wrap gap-1.5">
              {(attributes ?? []).map((attribute) => (
                <li key={attribute.id}>
                  {canManage ? (
                    <Menu
                      align="start"
                      trigger={
                        <button
                          type="button"
                          aria-label={t(text.actionsFor, { name: attribute.name })}
                          className={chipClassName}
                        >
                          {attribute.name}
                        </button>
                      }
                    >
                      <MenuLabel>
                        {attribute.caseCount + attribute.checklistCount > 0
                          ? t(text.usage, {
                              cases: attribute.caseCount,
                              checklists: attribute.checklistCount,
                            })
                          : t(text.notUsed)}
                      </MenuLabel>
                      <MenuItem onSelect={() => openEdit(attribute)}>
                        <Pencil className="size-3.5" />
                        {t('common.edit')}
                      </MenuItem>
                      <MenuItem destructive onSelect={() => setDeleting(attribute)}>
                        <Trash2 className="size-3.5" />
                        {t('common.delete')}
                      </MenuItem>
                    </Menu>
                  ) : (
                    <span className={chipClassName}>{attribute.name}</span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </CardBody>
      </Card>

      <CaseAttributeFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        projectCode={projectCode}
        kind={kind}
        attribute={editing}
      />

      <ConfirmDialog
        open={deleting !== undefined}
        onOpenChange={(open) => {
          if (!open) setDeleting(undefined);
        }}
        title={t(text.deleteTitle, { name: deleting?.name ?? '' })}
        description={t(text.deleteHint, {
          cases: deleting?.caseCount ?? 0,
          checklists: deleting?.checklistCount ?? 0,
        })}
        confirmLabel={t('common.delete')}
        loading={remove.isPending}
        onConfirm={() => void confirmDelete()}
      />
    </>
  );
};
